import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, GripVertical, Layers, Loader2, Pencil, Plus, Trash2, Upload, Video } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";
import { AdminModal, Field, Pill, inputCls, textareaCls } from "@/pages/admin/ui";

/**
 * Éditeur de formation partagé (admin + revendeur) — fonctions strictement identiques.
 * Le paramètre `endpoints` permet de cibler l'espace admin (/admin/...) ou revendeur (/vendor/...).
 *
 * endpoints = {
 *   formations: "/admin/formations",
 *   modules: "/admin/formation-modules",
 *   lessons: "/admin/formation-lessons",
 *   upload: (formationId) => `/admin/formations/upload?formation_id=${formationId||""}`,
 *   imageUpload: "/admin/upload",
 * }
 */

const LEVELS = ["Débutant", "Intermédiaire", "Avancé", "Tous niveaux"];
const ACCESS = [
  { value: "permanent", label: "Accès permanent (à vie)" },
  { value: "30", label: "30 jours" }, { value: "90", label: "90 jours" },
  { value: "180", label: "180 jours" }, { value: "365", label: "1 an" },
];

const emptyForm = {
  title: "", short_description: "", description: "", category: "", level: "Débutant", language: "Français",
  price: 0, old_price: 0, duration: "", image_url: "", logo_url: "", gallery: [],
  promo_video_source: "link", promo_video_url: "", instructor_name: "", instructor_bio: "",
  objectives: [], prerequisites: [], audience: [], certificate_enabled: true,
  access_duration: "permanent", status: "draft", is_featured: false,
};

const lines = (arr) => (arr || []).join("\n");
const toArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

function Toggle({ checked, onChange, label, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={() => onChange(!checked)}
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold transition-colors ${checked ? "border-brand bg-brand-light text-brand" : "border-line text-slate-500"}`}>
      <span className={`flex h-4 w-4 items-center justify-center rounded ${checked ? "bg-brand text-white" : "border border-slate-300"}`}>{checked && "✓"}</span>
      {label}
    </button>
  );
}

// ------------------------------------------------------------------ formation form
export function FormationFormModal({ open, onClose, editing, categories = [], onSaved, endpoints }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const videoRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    if (open) setForm(editing ? { ...emptyForm, ...editing } : emptyForm);
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadGallery = async (file) => {
    if (!file) return;
    const body = new FormData(); body.append("file", file);
    try {
      const { data } = await api.post(endpoints.imageUpload, body, { headers: { "Content-Type": "multipart/form-data" } });
      set("gallery", [...(form.gallery || []), data.url]);
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    setUploadingVideo(true);
    const body = new FormData(); body.append("file", file);
    try {
      const { data } = await api.post(endpoints.upload(editing?.id || ""), body, { headers: { "Content-Type": "multipart/form-data" } });
      set("promo_video_url", data.url); set("promo_video_source", "upload");
      toast.success("Vidéo téléversée");
    } catch (e) { toast.error(apiError(e)); } finally { setUploadingVideo(false); }
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Le titre est obligatoire"); return; }
    setSaving(true);
    const payload = {
      ...form, price: Number(form.price) || 0, old_price: Number(form.old_price) || 0,
      is_promo: Number(form.old_price) > Number(form.price),
    };
    try {
      if (editing) await api.put(`${endpoints.formations}/${editing.id}`, payload);
      else await api.post(endpoints.formations, payload);
      toast.success(editing ? "Formation mise à jour" : "Formation créée");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <AdminModal open={open} onOpenChange={onClose} title={editing ? "Modifier la formation" : "Ajouter une formation"} wide>
      <div className="space-y-4">
        <Field label="Nom de la formation">
          <input data-testid="formation-form-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex : Photoshop Professionnel" />
        </Field>
        <Field label="Description courte">
          <input data-testid="formation-form-short" className={inputCls} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} placeholder="Une phrase d'accroche" />
        </Field>
        <Field label="Description complète">
          <textarea data-testid="formation-form-desc" className={textareaCls} rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} />
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Catégorie">
            <select data-testid="formation-form-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
              <option value="">— Choisir —</option>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Niveau">
            <select className={inputCls} value={form.level} onChange={(e) => set("level", e.target.value)}>
              {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Langue">
            <input className={inputCls} value={form.language} onChange={(e) => set("language", e.target.value)} />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Prix (FCFA)"><input data-testid="formation-form-price" type="number" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} /></Field>
          <Field label="Ancien prix (FCFA)"><input type="number" className={inputCls} value={form.old_price} onChange={(e) => set("old_price", e.target.value)} /></Field>
          <Field label="Durée totale"><input className={inputCls} value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder="Ex : 8h 30m" /></Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <ImageUpload label="Image principale" value={form.image_url} onChange={(v) => set("image_url", v)} testId="formation-image" uploadUrl={endpoints.imageUpload} />
          <ImageUpload label="Logo / icône" value={form.logo_url} onChange={(v) => set("logo_url", v)} testId="formation-logo" uploadUrl={endpoints.imageUpload} />
        </div>

        <Field label="Galerie de photos">
          <div className="flex flex-wrap gap-2">
            {(form.gallery || []).map((g, i) => (
              <div key={i} className="relative h-16 w-24 overflow-hidden rounded-lg border border-line">
                <img src={fileUrl(g)} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => set("gallery", form.gallery.filter((_, j) => j !== i))} className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-slate-500"><Trash2 className="h-3 w-3" /></button>
              </div>
            ))}
            <button type="button" onClick={() => galleryRef.current?.click()} className="flex h-16 w-24 items-center justify-center rounded-lg border border-dashed border-line text-slate-400 hover:border-brand hover:text-brand"><Plus className="h-5 w-5" /></button>
            <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadGallery(e.target.files?.[0])} />
          </div>
        </Field>

        <Field label="Vidéo de présentation">
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <select className={`${inputCls} w-36`} value={form.promo_video_source} onChange={(e) => set("promo_video_source", e.target.value)}>
                <option value="link">Lien</option><option value="upload">Fichier</option>
              </select>
              <input className={inputCls} value={form.promo_video_url} onChange={(e) => set("promo_video_url", e.target.value)} placeholder="URL YouTube / Vimeo / MP4" />
              <button type="button" onClick={() => videoRef.current?.click()} disabled={uploadingVideo} className="flex shrink-0 items-center gap-1.5 rounded-lg border border-line px-3 text-xs font-bold text-ink hover:border-brand">
                {uploadingVideo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              </button>
              <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={(e) => uploadVideo(e.target.files?.[0])} />
            </div>
          </div>
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Objectifs (un par ligne)"><textarea className={textareaCls} rows={4} value={lines(form.objectives)} onChange={(e) => set("objectives", toArr(e.target.value))} /></Field>
          <Field label="Prérequis (un par ligne)"><textarea className={textareaCls} rows={4} value={lines(form.prerequisites)} onChange={(e) => set("prerequisites", toArr(e.target.value))} /></Field>
          <Field label="Public cible (un par ligne)"><textarea className={textareaCls} rows={4} value={lines(form.audience)} onChange={(e) => set("audience", toArr(e.target.value))} /></Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Formateur"><input className={inputCls} value={form.instructor_name} onChange={(e) => set("instructor_name", e.target.value)} /></Field>
          <Field label="Bio formateur"><input className={inputCls} value={form.instructor_bio} onChange={(e) => set("instructor_bio", e.target.value)} /></Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Durée d'accès">
            <select className={inputCls} value={form.access_duration} onChange={(e) => set("access_duration", e.target.value)}>
              {ACCESS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </Field>
          <Field label="Statut">
            <select data-testid="formation-form-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="draft">Brouillon</option><option value="published">Publiée</option><option value="unpublished">Non publiée</option>
            </select>
          </Field>
        </div>

        <div className="flex flex-wrap gap-2">
          <Toggle checked={form.certificate_enabled} onChange={(v) => set("certificate_enabled", v)} label="Certificat de formation" testId="formation-cert-toggle" />
          <Toggle checked={form.is_featured} onChange={(v) => set("is_featured", v)} label="Mise en avant" testId="formation-featured-toggle" />
        </div>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button onClick={onClose} className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="formation-form-save" className="flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Enregistrer" : "Créer la formation"}
          </button>
        </div>
      </div>
    </AdminModal>
  );
}

// ------------------------------------------------------------------ lesson editor
const emptyLesson = {
  title: "", description: "", duration: "", video_source: "none", video_url: "", video_file_id: "",
  text_content: "", images: [], files: [], links: [], notes: "",
  is_free_preview: false, is_mandatory: true, is_published: true,
};

function LessonModal({ open, onClose, formationId, moduleId, editing, onSaved, endpoints }) {
  const [l, setL] = useState(emptyLesson);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const vidRef = useRef(null);

  useEffect(() => { if (open) setL(editing ? { ...emptyLesson, ...editing } : emptyLesson); }, [open, editing]);
  const set = (k, v) => setL((s) => ({ ...s, [k]: v }));

  const upload = async (file, kind) => {
    if (!file) return;
    setBusy(true);
    const body = new FormData(); body.append("file", file);
    try {
      const { data } = await api.post(endpoints.upload(formationId), body, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "video") { set("video_source", "upload"); set("video_url", data.url); set("video_file_id", data.file_id); }
      else set("files", [...(l.files || []), data]);
      toast.success("Fichier téléversé");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!l.title.trim()) { toast.error("Titre obligatoire"); return; }
    setSaving(true);
    const payload = { ...l, formation_id: formationId, module_id: moduleId };
    try {
      if (editing) await api.put(`${endpoints.lessons}/${editing.id}`, payload);
      else await api.post(endpoints.lessons, payload);
      toast.success("Cours enregistré"); onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <AdminModal open={open} onOpenChange={onClose} title={editing ? "Modifier le cours" : "Ajouter un cours"} wide>
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-[1fr_140px]">
          <Field label="Titre du cours"><input data-testid="lesson-title" className={inputCls} value={l.title} onChange={(e) => set("title", e.target.value)} /></Field>
          <Field label="Durée"><input className={inputCls} value={l.duration} onChange={(e) => set("duration", e.target.value)} placeholder="12:45" /></Field>
        </div>
        <Field label="Description"><input className={inputCls} value={l.description} onChange={(e) => set("description", e.target.value)} /></Field>

        <Field label="🎥 Vidéo">
          <div className="flex gap-2">
            <select className={`${inputCls} w-32`} value={l.video_source} onChange={(e) => set("video_source", e.target.value)}>
              <option value="none">Aucune</option><option value="link">Lien</option><option value="upload">Fichier</option>
            </select>
            {l.video_source !== "none" && (
              <>
                <input className={inputCls} value={l.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="URL YouTube / Vimeo / MP4" />
                <button type="button" onClick={() => vidRef.current?.click()} disabled={busy} className="flex shrink-0 items-center rounded-lg border border-line px-3 hover:border-brand"><Upload className="h-4 w-4" /></button>
                <input ref={vidRef} type="file" accept="video/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], "video")} />
              </>
            )}
          </div>
        </Field>

        <Field label="📝 Contenu texte"><textarea className={textareaCls} rows={4} value={l.text_content} onChange={(e) => set("text_content", e.target.value)} /></Field>

        <Field label="📄 Fichiers / ressources">
          <div className="space-y-2">
            {(l.files || []).map((f, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm">
                <FileText className="h-4 w-4 text-brand" /><span className="flex-1 truncate">{f.name}</span>
                <button type="button" onClick={() => set("files", l.files.filter((_, j) => j !== i))} className="text-rose-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <div className="flex gap-2">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-2 text-xs font-bold text-ink hover:border-brand">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Téléverser un fichier
              </button>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0], "file")} />
            </div>
          </div>
        </Field>

        <Field label="🔗 Liens externes (label|url par ligne)">
          <textarea className={textareaCls} rows={2} value={(l.links || []).map((x) => `${x.label}|${x.url}`).join("\n")}
            onChange={(e) => set("links", e.target.value.split("\n").map((r) => { const [label, url] = r.split("|"); return { label: (label || "").trim(), url: (url || "").trim() }; }).filter((x) => x.url))} />
        </Field>

        <Field label="💡 Notes importantes"><textarea className={textareaCls} rows={2} value={l.notes} onChange={(e) => set("notes", e.target.value)} /></Field>

        <div className="flex flex-wrap gap-2">
          <Toggle checked={l.is_free_preview} onChange={(v) => set("is_free_preview", v)} label="Aperçu gratuit" testId="lesson-free-toggle" />
          <Toggle checked={l.is_mandatory} onChange={(v) => set("is_mandatory", v)} label="Obligatoire" />
          <Toggle checked={l.is_published} onChange={(v) => set("is_published", v)} label="Publié" />
        </div>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button onClick={onClose} className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="lesson-save" className="flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
          </button>
        </div>
      </div>
    </AdminModal>
  );
}

// ------------------------------------------------------------------ program builder
export function ProgramModal({ open, onClose, formation, endpoints }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lessonModal, setLessonModal] = useState(null);
  const [dragItem, setDragItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`${endpoints.formations}/${formation.id}`); setDetail(data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { if (open && formation) load(); /* eslint-disable-next-line */ }, [open, formation]);

  const addModule = async () => {
    const title = window.prompt("Titre du module", `Module ${(detail?.modules?.length || 0) + 1}`);
    if (!title) return;
    await api.post(endpoints.modules, { formation_id: formation.id, title });
    load();
  };
  const editModule = async (m) => {
    const title = window.prompt("Titre du module", m.title);
    if (title == null) return;
    await api.put(`${endpoints.modules}/${m.id}`, { formation_id: formation.id, title, description: m.description || "" });
    load();
  };
  const delModule = async (m) => { if (window.confirm(`Supprimer le module « ${m.title} » et ses cours ?`)) { await api.delete(`${endpoints.modules}/${m.id}`); load(); } };
  const delLesson = async (l) => { if (window.confirm(`Supprimer le cours « ${l.title} » ?`)) { await api.delete(`${endpoints.lessons}/${l.id}`); load(); } };

  const moveModule = async (mods) => {
    setDetail((d) => ({ ...d, modules: mods }));
    await api.post(`${endpoints.modules}/reorder`, { ids: mods.map((m) => m.id) });
  };
  const moveLesson = async (moduleId, lessons) => {
    setDetail((d) => ({ ...d, modules: d.modules.map((m) => (m.id === moduleId ? { ...m, lessons } : m)) }));
    await api.post(`${endpoints.lessons}/reorder`, { ids: lessons.map((l) => l.id) });
  };
  const onModuleDrop = (toIdx) => {
    if (!dragItem || dragItem.type !== "module" || dragItem.index === toIdx) return setDragItem(null);
    const mods = [...detail.modules];
    const [m] = mods.splice(dragItem.index, 1);
    mods.splice(toIdx, 0, m);
    moveModule(mods);
    setDragItem(null);
  };
  const onLessonDrop = (moduleId, toIdx) => {
    if (!dragItem || dragItem.type !== "lesson" || dragItem.moduleId !== moduleId || dragItem.index === toIdx) return setDragItem(null);
    const mod = detail.modules.find((m) => m.id === moduleId);
    const ls = [...mod.lessons];
    const [l] = ls.splice(dragItem.index, 1);
    ls.splice(toIdx, 0, l);
    moveLesson(moduleId, ls);
    setDragItem(null);
  };

  return (
    <AdminModal open={open} onOpenChange={onClose} title={`Programme — ${formation?.title || ""}`} wide>
      {loading || !detail ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : (
        <div className="space-y-3">
          <button onClick={addModule} data-testid="add-module-btn" className="flex items-center gap-2 rounded-lg bg-ink px-4 py-2.5 text-sm font-bold text-white"><Plus className="h-4 w-4" /> Ajouter un module</button>
          {detail.modules.length === 0 && <p className="rounded-xl border border-dashed border-line py-8 text-center text-sm text-slate-500">Aucun module. Ajoutez-en un pour construire le programme.</p>}
          {detail.modules.map((m, mi) => (
            <div
              key={m.id}
              className={`rounded-xl border border-line transition-opacity ${dragItem?.type === "module" && dragItem.index === mi ? "opacity-40" : ""}`}
              data-testid={`module-${m.id}`}
              onDragOver={(e) => { if (dragItem?.type === "module") e.preventDefault(); }}
              onDrop={() => onModuleDrop(mi)}
            >
              <div
                className="flex items-center gap-2 border-b border-line bg-slate-50 px-3 py-2.5"
                draggable
                onDragStart={(e) => { setDragItem({ type: "module", index: mi }); e.stopPropagation(); }}
                onDragEnd={() => setDragItem(null)}
              >
                <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-slate-400 active:cursor-grabbing" data-testid={`drag-module-${m.id}`} />
                <Layers className="h-4 w-4 text-brand" />
                <p className="flex-1 text-sm font-bold text-ink">{m.title}</p>
                <span className="text-xs text-slate-400">{m.lessons.length} cours</span>
                <button onClick={() => editModule(m)} className="text-slate-400 hover:text-brand"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => delModule(m)} className="text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="space-y-1 p-2">
                {m.lessons.map((l, li) => (
                  <div
                    key={l.id}
                    className={`flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 ${dragItem?.type === "lesson" && dragItem.moduleId === m.id && dragItem.index === li ? "opacity-40" : ""}`}
                    draggable
                    onDragStart={(e) => { setDragItem({ type: "lesson", moduleId: m.id, index: li }); e.stopPropagation(); }}
                    onDragEnd={() => setDragItem(null)}
                    onDragOver={(e) => { if (dragItem?.type === "lesson" && dragItem.moduleId === m.id) { e.preventDefault(); e.stopPropagation(); } }}
                    onDrop={(e) => { e.stopPropagation(); onLessonDrop(m.id, li); }}
                  >
                    <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-slate-300 active:cursor-grabbing" data-testid={`drag-lesson-${l.id}`} />
                    {l.video_source !== "none" ? <Video className="h-4 w-4 text-slate-400" /> : <FileText className="h-4 w-4 text-slate-400" />}
                    <span className="flex-1 text-sm text-slate-600">{l.title}</span>
                    {l.is_free_preview && <Pill tone="green">Aperçu</Pill>}
                    {!l.is_published && <Pill tone="slate">Masqué</Pill>}
                    <span className="text-xs text-slate-400">{l.duration}</span>
                    <button onClick={() => setLessonModal({ moduleId: m.id, editing: l })} className="text-slate-400 hover:text-brand"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => delLesson(l)} className="text-slate-400 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button onClick={() => setLessonModal({ moduleId: m.id, editing: null })} data-testid={`add-lesson-${m.id}`} className="flex items-center gap-1.5 rounded-lg border border-dashed border-line px-3 py-2 text-xs font-bold text-slate-500 hover:border-brand hover:text-brand"><Plus className="h-3.5 w-3.5" /> Ajouter un cours</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {lessonModal && (
        <LessonModal open onClose={() => setLessonModal(null)} formationId={formation.id} moduleId={lessonModal.moduleId} editing={lessonModal.editing} onSaved={load} endpoints={endpoints} />
      )}
    </AdminModal>
  );
}

export const ADMIN_FORMATION_ENDPOINTS = {
  formations: "/admin/formations",
  modules: "/admin/formation-modules",
  lessons: "/admin/formation-lessons",
  upload: (fid) => `/admin/formations/upload?formation_id=${fid || ""}`,
  imageUpload: "/admin/upload",
};

export const VENDOR_FORMATION_ENDPOINTS = {
  formations: "/vendor/formations",
  modules: "/vendor/formation-modules",
  lessons: "/vendor/formation-lessons",
  upload: (fid) => `/vendor/formations/upload?formation_id=${fid || ""}`,
  imageUpload: "/vendor/upload",
};
